import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";
import showToast from "../../utils/toastNotifications";

//! Fetch all successful payments for an offer
export const getOfferPayments = createAsyncThunk(
  "payment/getOfferPayments",
  async (offerId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/payments/offer/${offerId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch offer payments" }
      );
    }
  }
);

//! Fetch payment details for display
export const getPaymentDetailsForDisplay = createAsyncThunk(
  "payment/getPaymentDetailsForDisplay",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/payments/${paymentId}/display-details`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch payment details" }
      );
    }
  }
);

// NOTE: Create Payment for Order
export const createPayment = createAsyncThunk(
  "payment/createPayment",
  async (paymentData) => {
    const response = await api.post("payments", paymentData);
    return response.data;
  }
);

// NOTE: Create Payment Session
export const createPaymentSession = createAsyncThunk(
  "payment/createPaymentSession",
  async (paymentDetails, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "/payments/create-session",
        paymentDetails
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to create payment session" }
      );
    }
  }
);

// NOTE: Get User Payments with Filters
export const getUserPayments = createAsyncThunk(
  "payment/getUserPayments",
  async (filterData = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("payments/user", { params: filterData });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch user payments" }
      );
    }
  }
);

// Get Stripe Connect status for the current user
export const getStripeConnectStatus = createAsyncThunk(
  "stripe/getConnectStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/payments/stripe-status");
      console.log("Stripe Status Response:", response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to fetch Stripe Connect status",
        }
      );
    }
  }
);

// Initiate Stripe Connect onboarding for the current user
export const initiateStripeConnect = createAsyncThunk(
  "stripe/initiateConnect",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post("/payments/stripe-onboard");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to initiate Stripe Connect" }
      );
    }
  }
);

// Create Stripe Connect Account
export const createStripeConnect = createAsyncThunk(
  "user/createStripeConnect",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/payments/connect-stripe");
      console.log("Stripe connect URL generated:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error creating Stripe connect:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to create Stripe connection"
      );
    }
  }
);

// Check Stripe connection status after callback
export const checkStripeConnectionStatus = createAsyncThunk(
  "user/checkStripeConnectionStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/payments/stripe/callback");
      console.log("Stripe connection status:", response.data);
      return response.data;
    } catch (err) {
      console.error("Error checking Stripe status:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Failed to check Stripe connection status"
      );
    }
  }
);

export const processPointsWithdrawal = createAsyncThunk(
  "payment/processPointsWithdrawal",
  async (_, { rejectWithValue }) => {
    // Remove parameters since we're withdrawing all points
    try {
      const response = await api.post("/referral/withdraw-points");
      return response.data;
    } catch (err) {
      console.error("Error processing points withdrawal:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to process points withdrawal"
      );
    }
  }
);

export const getOrderPaymentSuccessDetails = createAsyncThunk(
  "/payment/getOrderPaymentSuccessDetails",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/payments/success-details/${paymentId}`);
      return response.data;
    } catch (err) {
      console.error("Error fetching order payment success details:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Failed to fetch order payment success details"
      );
    }
  }
);

export const fetchOrderPaymentFailureDetails = createAsyncThunk(
  "/payment/fetchOrderPaymentFailureDetails",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/payments/failure-details/${paymentId}`);
      return response.data;
    } catch (err) {
      console.error("Failed to fetch payment failure details:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch payment failure details"
      );
    }
  }
);

export const capturePaypalPayment = createAsyncThunk(
  "payments/capturePaypal",
  async ({ orderId }) => {
    const response = await api.post("/payments/capture-paypal", {
      orderId, // Send the PayPal 'token' as orderId
    });
    return response.data;
  }
);

// Get invoice/receipt data for a payment
export const getPaymentInvoiceData = createAsyncThunk(
  "payment/getPaymentInvoiceData",
  async (paymentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/payments/invoice/${paymentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch invoice data" }
      );
    }
  }
);

// Get redeemable points for checkout
export const getRedeemablePoints = createAsyncThunk(
  "referral/getRedeemablePoints",
  async ({ offerId, selectedAmount }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/referral/redeemable-points`, {
        params: {
          offerId,
          selectedAmount
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching redeemable points:", error);
      
      // Show toast only for user-facing errors, not for HTML responses
      if (error.response?.data?.message) {
        showToast("error", error.response.data.message);
      } else if (!error.response?.data) {
        // Likely got HTML instead of JSON (404 page, server error page)
        showToast("error", "Unable to fetch redeemable points. Please try again.");
      }
      
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch redeemable points" }
      );
    }
  }
);

// Redeem points (call this AFTER payment success)
export const redeemPoints = createAsyncThunk(
  "referral/redeemPoints",
  async ({ bonusIds, redeemedPoints }, { rejectWithValue }) => {
    try {
      const response = await fetch("/referral/redeem", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ bonusIds, redeemedPoints }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to redeem points");
      }

      showToast("success", "Points redeemed successfully!");
      return data.data;
    } catch (error) {
      console.error("Error redeeming points:", error);
      showToast("error", error.message);
      return rejectWithValue(error.message);
    }
  }
);

// ===============================================================================
// ====================== PAYHERE PAYMENT =======================================
// ===============================================================================

export const initiatePayherePayment = createAsyncThunk(
  "payments/initiatePayherePayment",
  async (paymentData) => {
      const response = await api.post("/payments/payhere/initiate", paymentData);
      return response.data;
  }
);

export const payhereNotify = createAsyncThunk(
  "payments/payhereNotify",
  async (paymentData) => {
      const response = await api.post("/payments/payhere/notify", paymentData);
      return response.data;
  }
);

export const createManualPayment = createAsyncThunk(
  "payment/createManualPayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      console.log("Creating manual payment:", paymentData);
      
      const response = await api.post("/payments/manual-payment", paymentData);
      console.log("Manual payment created:", response.data);
      
      return response.data;
    } catch (err) {
      console.error("Error creating manual payment:", err);
    
      let errorMessage;
    
      if (err.response && err.response.data) {
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } }
      
      return rejectWithValue({
        message: errorMessage,
        status: err.response?.status,
        data: err.response?.data,
      });
    }
  }
);